import { useState, useEffect } from 'react'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { getLocalStatus } from '@/api/profiles'

export type ConnectionState = 'checking' | 'connected' | 'error' | 'noDB' | 'noProxy'

export interface ConnectionStatus {
  state: ConnectionState
  profileName: string | null
  error: string | null
  refresh: () => void
}

// Poll aggressively while not yet connected; slow down once stable.
const POLL_INTERVAL_CONNECTING = 4_000   // 4s while checking / error (DB still initializing)
const POLL_INTERVAL_CONNECTED  = 30_000  // 30s once connected (heartbeat)

export function useConnectionStatus(): ConnectionStatus {
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()
  const [tick, setTick] = useState(0)
  const [status, setStatus] = useState<Omit<ConnectionStatus, 'refresh'>>({
    state: isElectron ? 'checking' : 'noProxy',
    profileName: null,
    error: null,
  })

  useEffect(() => {
    if (!isElectron || !proxyUrl) {
      setStatus({ state: 'noProxy', profileName: null, error: null })
      return
    }

    let cancelled = false
    let timerId: ReturnType<typeof setTimeout> | null = null
    let initializingCount = 0
    const MAX_INITIALIZING_POLLS = 6  // 6 × 4s = ~24s before showing a timeout error

    async function check() {
      try {
        const configRes = await fetch(`${proxyUrl}/local/config`, {
          headers: proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {},
          signal: AbortSignal.timeout(3000),
        })
        const config = await configRes.json() as { hasDatabaseUrl: boolean }
        if (cancelled) return

        if (!config.hasDatabaseUrl) {
          setStatus({ state: 'noDB', profileName: null, error: null })
          // noDB won't change without a restart — no need to keep polling
          return
        }

        const result = await getLocalStatus(proxyUrl!, proxyToken)
        if (cancelled) return

        if (result.connected) {
          initializingCount = 0
          setStatus({ state: 'connected', profileName: result.profileName ?? null, error: null })
          timerId = setTimeout(check, POLL_INTERVAL_CONNECTED)
        } else if (result.initializing) {
          initializingCount++
          if (initializingCount >= MAX_INITIALIZING_POLLS) {
            setStatus({ state: 'error', profileName: null, error: 'La app no pudo conectar a la base de datos — revisá la configuración en modo proveedor' })
            timerId = setTimeout(check, POLL_INTERVAL_CONNECTING)
          } else {
            setStatus({ state: 'checking', profileName: null, error: null })
            timerId = setTimeout(check, POLL_INTERVAL_CONNECTING)
          }
        } else {
          initializingCount = 0
          setStatus({ state: 'error', profileName: result.profileName ?? null, error: result.error ?? 'Sin conexión' })
          timerId = setTimeout(check, POLL_INTERVAL_CONNECTING)
        }
      } catch {
        if (!cancelled) {
          initializingCount = 0
          setStatus({ state: 'error', profileName: null, error: 'No se pudo verificar la conexión' })
          timerId = setTimeout(check, POLL_INTERVAL_CONNECTING)
        }
      }
    }

    setStatus(s => s.state === 'connected' ? s : { state: 'checking', profileName: null, error: null })
    check()
    return () => {
      cancelled = true
      if (timerId) clearTimeout(timerId)
    }
  }, [isElectron, proxyUrl, proxyToken, tick])

  return { ...status, refresh: () => setTick(t => t + 1) }
}
